import { z } from 'zod/v4'
import { clampReportDateRange, getCurrentReportDateFilters } from '@/lib/report-date-range'
import { getOrganizationNow } from '@/lib/reporting-dates'

const date = z.iso.date()
export const bookedSalesSearchSchema = z.object({
  start_date: date.optional().catch(undefined),
  end_date: date.optional().catch(undefined),
  day: date.optional().catch(undefined),
  page: z.coerce.number().int().positive().catch(1),
})
export const bookedSalesDaySearchSchema = bookedSalesSearchSchema.omit({ day: true }).extend({
  highlight_order_id: z.coerce.number().int().positive().optional().catch(undefined),
  // No page lets an order link open the page containing that order.
  page: z.coerce.number().int().positive().optional().catch(undefined),
})

export type BookedSalesSearch = z.infer<typeof bookedSalesSearchSchema>

/** `BookedSalesReportRequest::rules()`: what a Sales Summary request sends. */
export interface BookedSalesFilters {
  start_date: string
  end_date: string
  /** A day inside the range: order details list only that day. */
  day?: string
  /** Left out so an order link opens the page holding `highlight_order_id`. */
  page?: number | undefined
  highlight_order_id?: number
}

/** Either sales page's URL search; the day page has no `day` and may have no `page`. */
export function bookedSalesFilters(
  search: Partial<BookedSalesSearch>,
  timezone?: string | null
): BookedSalesFilters & { page: number } {
  const defaults = getCurrentReportDateFilters(getOrganizationNow(timezone))
  const start_date = search.start_date ?? defaults.start_date
  const end_date =
    search.end_date && search.end_date >= start_date
      ? search.end_date
      : start_date > defaults.end_date
        ? start_date
        : defaults.end_date
  const range = clampReportDateRange({ start_date, end_date }, undefined, defaults.end_date)
  return {
    ...range,
    ...(search.day && search.day >= range.start_date && search.day <= range.end_date
      ? { day: search.day }
      : {}),
    page: search.page ?? 1,
  }
}
